/**
 * Wesco Coatings — Cart Sync (v2 — parallel + per-seed)
 * ------------------------------------------------------
 * Keeps coating line item quantities in sync with their linked seed line items
 * across the cart drawer and the full cart page.
 *
 * Design principles:
 *   - Per-seed lock state in memory. No DOM reads on the click critical path.
 *   - No preflight fetchCart — clicks pass straight through to the theme.
 *   - Multiple seeds update in parallel. Seed A's lock does not block Seed B.
 *   - Rapid clicks on the same seed bounce off the in-memory lock instantly.
 *   - Coating removal/recalc batched per cart-changed event via /cart/update.js.
 *
 * Linking mechanism:
 *   Coating line items carry property _linked_seed_variant_id = the seed's
 *   variant ID. This sync script looks up the matching seed by variant ID.
 *
 * === WESCO COATING ADDON START ===
 */
(function () {
  'use strict';

  var LOG_PREFIX = '[Wesco Sync]';

  // ============================================================
  // CONFIG
  // ============================================================

  /**
   * Coating quantity is DERIVED, never customer-editable.
   *
   * ENFORCE_DERIVED_QTY: correct any coating whose qty != seed.qty * kg,
   *   not just when the seed qty changed. Without this, a coating edited
   *   directly (devtools, stale cart, legacy line) is never corrected
   *   because the seed never moved.
   *
   * ENFORCE_ON_LOAD: run one correction pass on page load, so a cart that
   *   drifted in a previous session is repaired before checkout.
   */
  var ENFORCE_DERIVED_QTY = true;
  var ENFORCE_ON_LOAD = true;

  /**
   * Hard ceiling on how long a line may sit in the busy state before the
   * watchdog clears it. Must comfortably exceed theme.js's 500ms debounce
   * plus a round-trip.
   */
  var LOCK_TIMEOUT_MS = 6000;

  // ============================================================
  // STATE
  // ============================================================

  /**
   * Per-seed snapshot, keyed by VARIANT ID (not line key — line keys can
   * change when an item is removed and re-added).
   *
   *   seedSnapshots[variantId] = {
   *     lastQty: number,      // last-known qty, used to detect changes
   *     locked: boolean,      // sync gate — true while update in flight
   *     pendingRemoval: bool, // true means: when sync runs, remove this seed's coating
   *     coatingKeys: string[] // line item keys of linked coatings
   *   }
   */
  var seedSnapshots = {};

  /**
   * Sync gate per LINE KEY (cart line item key) — used by the click
   * interceptors. Separate from variant-id-keyed snapshots because at
   * click time we have the line key, not the variant id.
   */
  var lockedLines = {};

  /**
   * Cache: seedLineKey -> [coatingLineKeys].
   * Rebuilt on every cart fetch. Used to instantly lock coating rows when
   * a seed button is clicked, without needing to wait on /cart.js.
   */
  var seedKeyToCoatings = {};

  /**
   * Reverse cache: seedLineKey -> seedVariantId.
   * Lets us map a click on a seed row directly to its variant id.
   */
  var seedKeyToVariantId = {};

  /**
   * Set of line keys that are coatings: { lineKey: true }.
   * Used by the Alpine interceptors to reject any quantity mutation
   * aimed at a coating line, whatever triggered it.
   */
  var coatingLineKeys = {};

  // ============================================================
  // CART API
  // ============================================================

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (r) { return r.json(); });
  }

  function changeLineQty(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart/change.js failed: ' + r.status);
      return r.json();
    });
  }

  /**
   * Apply multiple line-item quantity updates in a single API call.
   * `updatesByKey` is an object: { lineKey: newQty, lineKey: 0 (=remove), ... }
   *
   * This is much faster than firing parallel /cart/change.js calls because
   * Shopify processes the whole batch in one transaction and returns
   * fresh section HTML for free.
   */
  function batchUpdate(updatesByKey) {
    if (!(window.Alpine && Alpine.store && Alpine.store('xCartHelper'))) {
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ updates: updatesByKey })
      }).then(function (r) {
        if (!r.ok) throw new Error('cart/update.js failed: ' + r.status);
        return r.json();
      });
    }

    var sections = Alpine.store('xCartHelper').getSectionsToRender().map(function (s) {
      return s.id;
    });

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        updates: updatesByKey,
        sections: sections,
        sections_url: window.location.pathname
      })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart/update.js failed: ' + r.status);
      return r.json();
    });
  }

  function refreshCartUI(sectionsFromResponse) {
    if (!(window.Alpine && Alpine.store && Alpine.store('xCartHelper'))) {
      return Promise.resolve();
    }

    var sectionList = Alpine.store('xCartHelper').getSectionsToRender();

    // If sections came back with our batchUpdate response, use them directly.
    // Otherwise fetch them separately.
    var sectionsPromise;
    if (sectionsFromResponse) {
      sectionsPromise = Promise.resolve(sectionsFromResponse);
    } else {
      var sectionIds = sectionList.map(function (s) { return s.id; });
      sectionsPromise = fetch('/?sections=' + sectionIds.join(','), {
        credentials: 'same-origin'
      }).then(function (r) { return r.json(); });
    }

    return sectionsPromise.then(function (sectionsResponse) {
      sectionList.forEach(function (section) {
        var el = document.querySelector(section.selector);
        if (el && sectionsResponse[section.id]) {
          el.innerHTML = extractSectionInner(sectionsResponse[section.id], section.selector);
        }
      });

      var bubble = document.querySelector('#cart-icon-bubble span');
      if (bubble) {
        Alpine.store('xCartHelper').currentItemCount = parseInt(bubble.innerHTML, 10) || 0;
      }

      // Our own re-render does not fire eurus:cart:items-changed, so
      // re-apply the DOM lock here as well.
      if (typeof hardenCoatingRows === 'function') hardenCoatingRows();
    });
  }

  function extractSectionInner(html, selector) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var found = parsed.querySelector(selector);
    return found ? found.innerHTML : html;
  }

  // ============================================================
  // CART DATA HELPERS
  // ============================================================

  function isCoating(item) {
    return item && item.properties && item.properties._item_type === 'coating';
  }

  function linkedSeedVariantId(coatingItem) {
    if (!coatingItem || !coatingItem.properties) return null;
    var raw = coatingItem.properties._linked_seed_variant_id;
    if (!raw) return null;
    var n = parseInt(raw, 10);
    return isNaN(n) ? null : n;
  }

  function parseKgPerUnit(variantTitle) {
    if (!variantTitle) return 0;
    var lower = String(variantTitle).toLowerCase();
    var normalised = lower.split(' kg').join('kg');
    var parts = normalised.split('kg');
    if (parts.length < 2) return 0;
    var beforeKg = parts[0];
    var tokens = beforeKg.split(' ');
    var lastToken = tokens[tokens.length - 1].trim();
    var n = parseFloat(lastToken);
    if (isNaN(n) || n <= 0) return 0;
    return n;
  }

  /**
   * Rebuild the seedKey/variant caches from a fresh cart snapshot.
   * Called on init and after every cart mutation.
   */
  function rebuildCaches(items) {
    seedKeyToCoatings = {};
    seedKeyToVariantId = {};
    coatingLineKeys = {};

    items.forEach(function (item) {
      if (isCoating(item)) coatingLineKeys[item.key] = true;
    });

    items.forEach(function (seedItem) {
      if (isCoating(seedItem)) return;

      seedKeyToVariantId[seedItem.key] = seedItem.variant_id;

      var coatingKeys = [];
      items.forEach(function (item) {
        if (isCoating(item) && linkedSeedVariantId(item) === seedItem.variant_id) {
          coatingKeys.push(item.key);
        }
      });
      seedKeyToCoatings[seedItem.key] = coatingKeys;
    });
  }

  /**
   * Ensure a snapshot exists for this seed. Returns the snapshot.
   * Used both for initial baseline and ad-hoc lookups.
   */
  function getSnapshot(variantId) {
    if (!seedSnapshots[variantId]) {
      seedSnapshots[variantId] = {
        lastQty: undefined,
        locked: false,
        pendingRemoval: false,
        coatingKeys: []
      };
    }
    return seedSnapshots[variantId];
  }

  // ============================================================
  // DOM VISUAL HELPERS
  // ============================================================

  function cssEscape(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, function (c) {
      return '\\' + c;
    });
  }

  function findLineItemRow(itemKey) {
    var qtyEl = document.getElementById('cart-qty-' + itemKey);
    if (!qtyEl) return null;
    var el = qtyEl.parentElement;
    while (el && el !== document.body) {
      if (el.querySelector('#remove-' + cssEscape(itemKey))) return el;
      el = el.parentElement;
    }
    return qtyEl.closest('[data-cart-item], li, tr, div') || qtyEl.parentElement;
  }

  function showLoading(itemKey) {
    var loadingEl = document.getElementById('loading-' + itemKey);
    var removeEl = document.getElementById('remove-' + itemKey);
    if (loadingEl) loadingEl.classList.remove('hidden');
    if (removeEl) removeEl.style.display = 'none';
  }

  function hideLoading(itemKey) {
    var loadingEl = document.getElementById('loading-' + itemKey);
    var removeEl = document.getElementById('remove-' + itemKey);
    if (loadingEl) loadingEl.classList.add('hidden');
    if (removeEl) removeEl.style.display = '';
  }

  function applyVisualLock(itemKey) {
    var row = findLineItemRow(itemKey);
    if (!row) return;

    var escapedKey = cssEscape(itemKey);

    var removeBtn = row.querySelector('#remove-' + escapedKey);
    if (removeBtn) {
      removeBtn.dataset.wescoLocked = '1';
      removeBtn.style.pointerEvents = 'none';
      removeBtn.style.opacity = '0.5';
    }

    var buttons = row.querySelectorAll('button');
    buttons.forEach(function (btn) {
      var clickAttr = btn.getAttribute('@click.prevent') || btn.getAttribute('@click') || '';
      if (clickAttr.indexOf(itemKey) !== -1 &&
          (clickAttr.indexOf('plusItemQty') !== -1 || clickAttr.indexOf('minusItemQty') !== -1)) {
        btn.dataset.wescoLocked = '1';
        btn.style.pointerEvents = 'none';
        btn.style.opacity = '0.5';
      }
    });
  }

  function removeVisualLock(itemKey) {
    var row = findLineItemRow(itemKey);
    if (!row) return;
    var locked = row.querySelectorAll('[data-wesco-locked="1"]');
    locked.forEach(function (el) {
      delete el.dataset.wescoLocked;
      el.style.pointerEvents = '';
      el.style.opacity = '';
    });
  }

  // ============================================================
  // LOCK PRIMITIVES — in-memory, synchronous, fast
  // ============================================================

  /**
   * Mark a seed line as syncing and apply the visual busy state.
   *
   * NON-BLOCKING BY DESIGN. This used to return false when the line was
   * already locked, which bounced the click. That produced dead steppers:
   * the line-key lock was always set, but the release path is keyed on
   * variant id via the snapshot, so any click landing while
   * seedKeyToVariantId was cold or stale locked the line permanently.
   *
   * Blocking is not needed for correctness. theme.js already debounces
   * 500ms per line, and syncCoatings() recomputes coating quantities from
   * the live cart on every pass, so a redundant pass is idempotent rather
   * than wrong. Always returns true.
   */
  function tryAcquireSeedLock(seedLineKey) {
    lockedLines[seedLineKey] = true;

    var coatingKeys = seedKeyToCoatings[seedLineKey] || [];
    coatingKeys.forEach(function (coatingKey) {
      lockedLines[coatingKey] = true;
      showLoading(coatingKey);
    });

    // Flag the variant-keyed snapshot so reapplyLocks works after refresh
    var variantId = seedKeyToVariantId[seedLineKey];
    if (variantId !== undefined) {
      var snap = getSnapshot(variantId);
      snap.locked = true;
      snap.coatingKeys = coatingKeys.slice();
    }

    // Watchdog: never let a line stay busy. If a sync pass is dropped
    // (failed request, missing items-changed event), clear it anyway.
    scheduleLockWatchdog(seedLineKey, coatingKeys);

    return true;
  }

  /**
   * Force-clear a line's busy state after LOCK_TIMEOUT_MS regardless of
   * what the sync pass did. Backstop against a permanently stuck row.
   */
  var lockWatchdogs = {};

  function scheduleLockWatchdog(seedLineKey, coatingKeys) {
    if (lockWatchdogs[seedLineKey]) clearTimeout(lockWatchdogs[seedLineKey]);

    lockWatchdogs[seedLineKey] = setTimeout(function () {
      delete lockWatchdogs[seedLineKey];
      if (!lockedLines[seedLineKey]) return;

      console.warn(LOG_PREFIX, 'Lock watchdog cleared stale lock on', seedLineKey);
      delete lockedLines[seedLineKey];
      removeVisualLock(seedLineKey);

      (coatingKeys || []).forEach(function (coatingKey) {
        delete lockedLines[coatingKey];
        hideLoading(coatingKey);
        removeVisualLock(coatingKey);
      });

      var variantId = seedKeyToVariantId[seedLineKey];
      if (variantId !== undefined && seedSnapshots[variantId]) {
        seedSnapshots[variantId].locked = false;
      }
    }, LOCK_TIMEOUT_MS);
  }

  /**
   * Release a seed's lock. Called when sync completes for this seed.
   */
  function releaseSeedLock(seedVariantId) {
    var snap = seedSnapshots[seedVariantId];
    if (!snap) return;

    snap.locked = false;
    var keys = snap.coatingKeys || [];
    keys.forEach(function (coatingKey) {
      delete lockedLines[coatingKey];
      hideLoading(coatingKey);
      removeVisualLock(coatingKey);
    });

    // Also clear seed line key locks owned by this variant
    Object.keys(seedKeyToVariantId).forEach(function (seedKey) {
      if (seedKeyToVariantId[seedKey] === seedVariantId) {
        delete lockedLines[seedKey];
        removeVisualLock(seedKey);
        if (lockWatchdogs[seedKey]) {
          clearTimeout(lockWatchdogs[seedKey]);
          delete lockWatchdogs[seedKey];
        }
      }
    });

    snap.coatingKeys = [];
  }

  /**
   * After a section re-render, re-apply visual locks for any seed
   * currently mid-sync. In-memory lock state survives the refresh;
   * we just need to push it back into the new DOM.
   */
  function reapplyLocksAfterRefresh() {
    Object.keys(seedSnapshots).forEach(function (variantId) {
      var snap = seedSnapshots[variantId];
      if (!snap.locked && !snap.pendingRemoval) return;

      snap.coatingKeys.forEach(function (coatingKey) {
        applyVisualLock(coatingKey);
        showLoading(coatingKey);
      });

      // Seed rows are deliberately NOT visually locked — their stepper
      // must stay clickable at all times. Only coating rows show busy state.
    });
  }

  // ============================================================
  // POST-MUTATION SYNC
  // ============================================================

  /**
   * Runs after every eurus:cart:items-changed event.
   * Compares current cart against per-seed snapshots; fires batch update
   * for any coatings that need to change.
   *
   * NO global syncing flag. Multiple invocations CAN run in parallel —
   * the batch update is atomic per cart-changed event, so race conditions
   * just mean redundant work, not incorrect state.
   */
  function syncCoatings() {
    fetchCart().then(function (cart) {
      var items = cart.items || [];
      rebuildCaches(items);

      // Map seeds by variant id
      var seedByVariant = {};
      items.forEach(function (item) {
        if (isCoating(item)) return;
        seedByVariant[item.variant_id] = item;
      });

      // Collect coating updates to apply
      var updatesByKey = {};
      var affectedVariantIds = [];

      items.forEach(function (item) {
        if (!isCoating(item)) return;

        var seedVariantId = linkedSeedVariantId(item);
        var coatingQty = item.quantity;

        if (seedVariantId === null) return;

        var snap = seedSnapshots[seedVariantId];

        // Case A: seed was removed via batch remove — drop coating
        if (snap && snap.pendingRemoval) {
          updatesByKey[item.key] = 0;
          if (affectedVariantIds.indexOf(seedVariantId) === -1) {
            affectedVariantIds.push(seedVariantId);
          }
          return;
        }

        // Case B: seed gone from cart — leave coating alone
        if (!seedByVariant[seedVariantId]) return;

        // Case C: seed in cart — coating qty is derived from the seed.
        var seed = seedByVariant[seedVariantId];
        var kg = parseKgPerUnit(seed.variant_title || (seed.variant && seed.variant.title));
        if (kg === 0) return;

        // Shopify line quantities are integers. A decimal kg variant
        // (e.g. "1.5kg") can produce a fractional target, which would never
        // equal the stored quantity and would re-fire on every sync pass.
        // Round to the nearest whole unit so the value can settle.
        var rawExpected = seed.quantity * kg;
        var expected = Math.round(rawExpected);
        if (rawExpected !== expected) {
          console.warn(LOG_PREFIX, 'Fractional coating qty', rawExpected,
                       'for', seed.variant_title, '- rounded to', expected);
        }
        if (expected < 1) return;

        var previousQty = snap ? snap.lastQty : undefined;
        var seedQtyChanged = previousQty !== undefined && previousQty !== seed.quantity;

        // With ENFORCE_DERIVED_QTY the seed does not have to have moved.
        // Any drift between the coating qty and seed.qty * kg is corrected,
        // which is what stops a hand-edited coating from sticking.
        var shouldCorrect = ENFORCE_DERIVED_QTY ? true : seedQtyChanged;

        if (shouldCorrect && expected !== coatingQty) {
          console.log(LOG_PREFIX, 'Recalc', item.key, ':', coatingQty, '->', expected);
          updatesByKey[item.key] = expected;
          if (affectedVariantIds.indexOf(seedVariantId) === -1) {
            affectedVariantIds.push(seedVariantId);
          }
        }
      });

      // Update baselines for every seed currently in cart
      items.forEach(function (item) {
        if (isCoating(item)) return;
        var snap = getSnapshot(item.variant_id);
        snap.lastQty = item.quantity;
      });

      // Clear pendingRemoval flags
      Object.keys(seedSnapshots).forEach(function (vid) {
        if (seedSnapshots[vid].pendingRemoval) {
          seedSnapshots[vid].pendingRemoval = false;
        }
      });

      // Nothing to update — release any optimistic locks that were set
      // when the user clicked but no coating change resulted
      if (Object.keys(updatesByKey).length === 0) {
        Object.keys(seedSnapshots).forEach(function (vid) {
          var snap = seedSnapshots[vid];
          if (snap.locked) {
            releaseSeedLock(parseInt(vid, 10));
          }
        });
        return;
      }

      // Show loading + lock on all coatings we're about to update
      Object.keys(updatesByKey).forEach(function (coatingKey) {
        showLoading(coatingKey);
        applyVisualLock(coatingKey);
      });

      // Fire ONE batch /cart/update.js with section render
      batchUpdate(updatesByKey)
        .then(function (response) {
          // The batchUpdate response includes the rendered sections —
          // use them directly to avoid a second network round-trip
          return refreshCartUI(response.sections);
        })
        .then(function () { return fetchCart(); })
        .then(function (cart) {
          var freshItems = cart.items || [];
          rebuildCaches(freshItems);

          // Refresh baselines
          freshItems.forEach(function (item) {
            if (!isCoating(item)) {
              getSnapshot(item.variant_id).lastQty = item.quantity;
            }
          });

          // Release locks for every affected seed
          affectedVariantIds.forEach(function (vid) {
            releaseSeedLock(vid);
          });
        })
        .catch(function (err) {
          console.error(LOG_PREFIX, 'Sync failed:', err);
          affectedVariantIds.forEach(function (vid) {
            releaseSeedLock(vid);
          });
        });
    }).catch(function (err) {
      console.error(LOG_PREFIX, 'Could not fetch cart:', err);
    });
  }

  // ============================================================
  // SEED REMOVAL (batch — bypasses theme.js for speed)
  // ============================================================

  /**
   * Remove a seed and its linked coatings in a single batch update.
   * Much faster than letting theme.js remove the seed (500ms debounce) +
   * then us removing the coating (another round-trip).
   *
   * Returns a Promise. Caller passes a callback that tells the wrapper
   * whether to also run the original Alpine method.
   *
   * If the line has no linked coating, callback(true) — let the theme
   * handle the removal. If it has a linked coating, we batch-remove
   * and callback(false) — we've already done the work.
   */
  function handleSeedRemoval(seedLineKey, callback) {
    var seedVariantId = seedKeyToVariantId[seedLineKey];
    var coatingKeys = seedKeyToCoatings[seedLineKey] || [];

    // No linked coating — let theme.js handle it
    if (coatingKeys.length === 0) {
      callback(true);
      return;
    }

    // Mark the seed for removal so syncCoatings doesn't try to recalc
    if (seedVariantId !== undefined) {
      var snap = getSnapshot(seedVariantId);
      snap.pendingRemoval = true;
    }

    // Show loading + lock on everything being removed
    showLoading(seedLineKey);
    applyVisualLock(seedLineKey);
    coatingKeys.forEach(function (k) {
      showLoading(k);
      applyVisualLock(k);
    });

    var updatesByKey = {};
    updatesByKey[seedLineKey] = 0;
    coatingKeys.forEach(function (k) { updatesByKey[k] = 0; });

    console.log(LOG_PREFIX, 'Batch remove:', Object.keys(updatesByKey));

    batchUpdate(updatesByKey)
      .then(function (response) {
        return refreshCartUI(response.sections);
      })
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        rebuildCaches(cart.items || []);
        if (seedVariantId !== undefined) {
          delete seedSnapshots[seedVariantId];
        }
        // Refresh baselines for remaining seeds
        (cart.items || []).forEach(function (item) {
          if (!isCoating(item)) {
            getSnapshot(item.variant_id).lastQty = item.quantity;
          }
        });
      })
      .catch(function (err) {
        console.error(LOG_PREFIX, 'Batch remove failed:', err);
        if (seedVariantId !== undefined) {
          delete seedSnapshots[seedVariantId];
        }
      });

    // We handled it — don't run the original Alpine method
    callback(false);
  }

  // ============================================================
  // ALPINE METHOD INTERCEPTORS
  // ============================================================

  function readDomQty(itemKey) {
    var el = document.getElementById('cart-qty-' + itemKey);
    if (!el) return null;
    var n = parseInt(el.value, 10);
    return isNaN(n) ? null : n;
  }

  /**
   * True if this line key belongs to a coating.
   *
   * Checks the in-memory cache first (authoritative, rebuilt on every cart
   * fetch), then falls back to the DOM marker rendered by the Liquid
   * templates — which covers the window between page load and the first
   * successful /cart.js response.
   */
  function isCoatingLineKey(itemKey) {
    if (coatingLineKeys[itemKey]) return true;
    var qtyEl = document.getElementById('cart-qty-' + itemKey);
    if (qtyEl && qtyEl.closest && qtyEl.closest('[data-wesco-coating="true"]')) return true;
    return false;
  }

  /**
   * Guard for every wrapped Alpine cart method. Coating quantities are
   * derived from their seed, so any direct mutation is rejected outright.
   */
  function rejectIfCoating(itemKey, methodName) {
    if (!isCoatingLineKey(itemKey)) return false;
    console.warn(LOG_PREFIX, 'Blocked', methodName, 'on coating line', itemKey,
                 '— coating qty is derived from its linked seed.');
    return true;
  }

  /**
   * Install our wrappers around Alpine's cart methods. The wrappers:
   *   1. Check the in-memory lock (instant — no DOM read)
   *   2. Acquire the lock if free (synchronous — no fetchCart)
   *   3. Either delegate to theme.js OR handle removal directly
   *
   * Critical: NO async work in the click path. The click acquires the
   * lock and fires the action in the same tick. Rapid clicks bounce off
   * the in-memory lock instantly.
   */
  function installInterceptors() {
    var alpineEls = document.querySelectorAll('[x-data]');

    alpineEls.forEach(function (el) {
      if (el.__wescoSyncWrapped) return;

      var tryWrap = function () {
        if (!el._x_dataStack || !el._x_dataStack.length) return false;
        var data = el._x_dataStack[0];

        if (typeof data.removeItem === 'function' && !data.__wescoRemoveWrapped) {
          var origRemove = data.removeItem.bind(data);
          data.removeItem = function (itemId, line, giftWrappingItemId) {
            // A coating has no coating of its own, so there is nothing to batch.
            // Hand it straight to the theme's normal removal.
            if (isCoatingLineKey(itemId)) {
              origRemove(itemId, line, giftWrappingItemId);
              return;
            }
            if (!tryAcquireSeedLock(itemId)) return; // rapid-click bounce

            handleSeedRemoval(itemId, function (shouldProceed) {
              if (shouldProceed) origRemove(itemId, line, giftWrappingItemId);
            });
          };
          data.__wescoRemoveWrapped = true;
        }

        if (typeof data.minusItemQty === 'function' && !data.__wescoMinusWrapped) {
          var origMinus = data.minusItemQty.bind(data);
          data.minusItemQty = function (itemId, line, ip, ti, mq, gwid) {
            if (rejectIfCoating(itemId, 'minusItemQty')) return;
            if (!tryAcquireSeedLock(itemId)) return;

            var currentQty = readDomQty(itemId);
            if (currentQty === 1) {
              // qty 1 -> 0 = removal
              handleSeedRemoval(itemId, function (shouldProceed) {
                if (shouldProceed) origMinus(itemId, line, ip, ti, mq, gwid);
              });
              return;
            }
            // Normal decrement — straight through to theme.js
            origMinus(itemId, line, ip, ti, mq, gwid);
          };
          data.__wescoMinusWrapped = true;
        }

        if (typeof data.plusItemQty === 'function' && !data.__wescoPlusWrapped) {
          var origPlus = data.plusItemQty.bind(data);
          data.plusItemQty = function (itemId, line, ip, ti, mq, gwid) {
            if (rejectIfCoating(itemId, 'plusItemQty')) return;
            if (!tryAcquireSeedLock(itemId)) return;

            // Straight through to theme.js — sync runs from cart-changed event
            origPlus(itemId, line, ip, ti, mq, gwid);
          };
          data.__wescoPlusWrapped = true;
        }

        if (typeof data.updateItemQty === 'function' && !data.__wescoUpdateWrapped) {
          var origUpdate = data.updateItemQty.bind(data);
          data.updateItemQty = function (itemId, line, ip, ti, mq, gwid) {
            if (rejectIfCoating(itemId, 'updateItemQty')) return;
            if (!tryAcquireSeedLock(itemId)) return;

            var newQty = readDomQty(itemId);
            if (newQty === 0) {
              handleSeedRemoval(itemId, function (shouldProceed) {
                if (shouldProceed) origUpdate(itemId, line, ip, ti, mq, gwid);
              });
              return;
            }
            origUpdate(itemId, line, ip, ti, mq, gwid);
          };
          data.__wescoUpdateWrapped = true;
        }

        return true;
      };

      if (tryWrap()) {
        el.__wescoSyncWrapped = true;
      } else {
        var retries = 0;
        var iv = setInterval(function () {
          retries++;
          if (tryWrap() || retries > 20) {
            clearInterval(iv);
            el.__wescoSyncWrapped = true;
          }
        }, 100);
      }
    });
  }

  // ============================================================
  // DOM QUANTITY LOCK
  // ============================================================

  /**
   * Belt-and-braces layer over the Liquid templates. The templates already
   * omit the stepper and remove button on coating rows, but Eurus re-renders
   * cart sections from server HTML on every mutation and Alpine re-binds
   * afterwards. This listener sits on document in the capture phase, so it
   * runs before any Alpine handler and can cancel the event outright if a
   * control ever reappears inside a coating row.
   */
  var COATING_ROW = '[data-wesco-coating="true"]';
  var CONTROL_HINT = /plusItemQty|minusItemQty|updateItemQty/;

  function coatingControlFor(target) {
    if (!target || !target.closest) return null;
    if (!target.closest(COATING_ROW)) return null;

    var ctrl = target.closest('button, a, input, [role="button"]');
    if (!ctrl) return null;

    if (ctrl.tagName === 'INPUT') {
      return ctrl.type === 'number' || /cart-qty|cart-item-qty/.test(ctrl.className || '')
        ? ctrl
        : null;
    }

    var attrs = [
      ctrl.getAttribute('@click.prevent'),
      ctrl.getAttribute('@click'),
      ctrl.getAttribute('x-on:click'),
      ctrl.getAttribute('x-on:change'),
      ctrl.getAttribute('aria-label')
    ].join(' ');

    return CONTROL_HINT.test(attrs) ? ctrl : null;
  }

  function swallowCoatingInteraction(e) {
    // Seed quantity inputs are display-only — the +/- buttons are the
    // only way to change quantity. Swallow direct entry attempts.
    var readonlyInput = e.target && e.target.closest
      ? e.target.closest('[data-wesco-qty-readonly]')
      : null;
    if (readonlyInput) {
      // Allow copy/select, block anything that would alter the value.
      if (e.type === 'keydown' && (e.metaKey || e.ctrlKey)) return;
      if (e.type === 'keydown' && /^(Tab|Escape|Arrow(Left|Right)|Home|End)$/.test(e.key)) return;
      e.preventDefault();
      if (e.type === 'wheel' || e.type === 'keydown') e.stopPropagation();
      return;
    }

    if (!coatingControlFor(e.target)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
  }

  /**
   * Make any stray quantity input inside a coating row non-editable.
   * Idempotent — safe to call after every re-render.
   */
  function hardenCoatingRows(root) {
    var scope = root || document;
    var rows = scope.querySelectorAll ? scope.querySelectorAll(COATING_ROW) : [];
    Array.prototype.forEach.call(rows, function (row) {
      Array.prototype.forEach.call(row.querySelectorAll('input[type="number"]'), function (input) {
        input.readOnly = true;
        input.tabIndex = -1;
        input.setAttribute('aria-readonly', 'true');
      });
      row.classList.add('wesco-qty-locked');
    });
  }

  function installDomQtyLock() {
    ['click', 'mousedown', 'keydown', 'wheel', 'change', 'input', 'paste'].forEach(function (evt) {
      document.addEventListener(evt, swallowCoatingInteraction, {
        capture: true,
        passive: false
      });
    });

    var style = document.createElement('style');
    style.setAttribute('data-wesco-coating-lock', '');
    style.textContent =
      COATING_ROW + ' .wesco-coating-qty { user-select: none; }' +
      COATING_ROW + '.wesco-qty-locked input[type="number"] { pointer-events: none; opacity: 0.6; }' +
      '[data-wesco-qty-readonly] { cursor: default; caret-color: transparent; }' +
      '[data-wesco-qty-readonly]::-webkit-outer-spin-button,' +
      '[data-wesco-qty-readonly]::-webkit-inner-spin-button' +
      ' { -webkit-appearance: none; margin: 0; }' +
      '[data-wesco-qty-readonly] { -moz-appearance: textfield; }';
    document.head.appendChild(style);

    hardenCoatingRows();
  }

  // ============================================================
  // BOOTSTRAP
  // ============================================================

  function init() {
    // Initial cart load — snapshot every seed's qty BEFORE any click can happen.
    // This is what makes the very first click work.
    fetchCart().then(function (cart) {
      var items = cart.items || [];
      rebuildCaches(items);
      items.forEach(function (item) {
        if (!isCoating(item)) {
          getSnapshot(item.variant_id).lastQty = item.quantity;
        }
      });

      // Repair a cart that drifted out of sync in a previous session
      // (legacy line items, direct API edits, abandoned-cart restores).
      if (ENFORCE_ON_LOAD) syncCoatings();
    });

    installInterceptors();
    installDomQtyLock();

    document.addEventListener('eurus:cart:items-changed', function () {
      reapplyLocksAfterRefresh();
      syncCoatings();
      hardenCoatingRows();
      // After section re-render the Alpine components are new — re-wrap them
      setTimeout(function () {
        installInterceptors();
        hardenCoatingRows();
      }, 400);
    });

    document.addEventListener('shopify:section:load', function () {
      setTimeout(function () {
        installInterceptors();
        hardenCoatingRows();
      }, 100);
    });

    console.log(LOG_PREFIX, 'Initialised');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
/* === WESCO COATING ADDON END === */